// Variant 6's finale: the lasers write the landing's title in the air in front of the
// telescope. The trace is taken
// from the live title itself — every span of it rasterised in its own computed font,
// weight and letter-spacing at its own place on screen — so the outline lands exactly
// where the logo sits in every other take, at any size. The glyphs' outlines become
// closed loops (marching squares over the raster), ordered left to right; a pen runs
// along them and the trace it leaves glows on a 2D canvas over the WebGL one.

type Pt = { x: number; y: number };
type Loop = { pts: Pt[]; length: number; minX: number };

const CELL = 1.5; // CSS px per sample: fine enough for the thin weights of the logo
const JUMP = 0.004; // the share of the run spent blanked, hopping between loops

export class LaserWriter {
  private loops: Loop[] = [];
  private total = 0;
  private drawn = 0; // progress already on the canvas
  private ctx: CanvasRenderingContext2D;
  private width = 0;
  private height = 0;
  ready = false;

  constructor(
    private canvas: HTMLCanvasElement,
    private title: HTMLElement,
    private color: string,
  ) {
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('no 2d context');
    this.ctx = ctx;
  }

  // Rasterise the title where it stands and trace its glyphs. Call again on resize.
  async prepare() {
    await document.fonts.ready;
    const host = this.canvas.getBoundingClientRect();
    this.width = host.width;
    this.height = host.height;
    const ratio = Math.min(window.devicePixelRatio || 1, 2);
    this.canvas.width = Math.round(host.width * ratio);
    this.canvas.height = Math.round(host.height * ratio);
    this.ctx.setTransform(ratio, 0, 0, ratio, 0, 0);

    const box = this.title.getBoundingClientRect();
    const pad = 6;
    const cols = Math.ceil((box.width + pad * 2) / CELL);
    const rows = Math.ceil((box.height + pad * 2) / CELL);
    const raster = document.createElement('canvas');
    raster.width = cols;
    raster.height = rows;
    const r = raster.getContext('2d', { willReadFrequently: true });
    if (!r) return;
    r.scale(1 / CELL, 1 / CELL);
    r.fillStyle = '#fff';
    // each leaf span keeps its own weight ("platanus hack" light, "[26]" medium)
    const leaves = [...this.title.querySelectorAll('span')].filter((s) => !s.querySelector('span') && s.textContent?.trim());
    for (const span of leaves.length ? leaves : [this.title]) {
      const style = getComputedStyle(span);
      const rect = span.getBoundingClientRect();
      r.font = `${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
      (r as CanvasRenderingContext2D & { letterSpacing: string }).letterSpacing = style.letterSpacing === 'normal' ? '0px' : style.letterSpacing;
      r.textBaseline = 'alphabetic';
      const text = span.textContent ?? '';
      const ascent = r.measureText(text).fontBoundingBoxAscent;
      r.fillText(text, rect.left - box.left + pad, rect.top - box.top + pad + ascent);
    }
    const data = r.getImageData(0, 0, cols, rows).data;
    const inside = (x: number, y: number) => x >= 0 && y >= 0 && x < cols && y < rows && data[(y * cols + x) * 4 + 3] > 110;

    const ox = box.left - host.left - pad;
    const oy = box.top - host.top - pad;
    this.loops = traceLoops(cols, rows, inside).map((pts) => {
      const mapped = pts.map((p) => ({ x: ox + p.x * CELL, y: oy + p.y * CELL }));
      let length = 0;
      for (let i = 1; i < mapped.length; i++) length += Math.hypot(mapped[i].x - mapped[i - 1].x, mapped[i].y - mapped[i - 1].y);
      return { pts: mapped, length, minX: Math.min(...mapped.map((p) => p.x)) };
    });
    // letter by letter: outer outline and its counters together, left to right
    this.loops.sort((a, b) => a.minX - b.minX);
    this.total = this.loops.reduce((sum, l) => sum + l.length, 0);
    this.ready = this.total > 0;
    // redraw what was already written, at the new size
    const progress = this.drawn;
    this.clear();
    if (progress > 0) this.draw(progress);
  }

  // The world needs one point to aim at: the middle of the title.
  center(): Pt {
    const box = this.title.getBoundingClientRect();
    const host = this.canvas.getBoundingClientRect();
    return { x: box.left - host.left + box.width / 2, y: box.top - host.top + box.height / 2 };
  }

  get size() {
    return { width: this.width, height: this.height };
  }

  // Where the pen is at progress u (0..1), and whether the beam is on (off while it hops
  // from one loop to the next).
  pen(u: number): Pt & { on: boolean } {
    if (!this.ready) return { ...this.center(), on: false };
    const n = this.loops.length;
    const per = (1 - JUMP * n) / this.total;
    let t = Math.min(1, Math.max(0, u));
    for (let i = 0; i < n; i++) {
      const loop = this.loops[i];
      const span = loop.length * per;
      if (t <= span) return { ...along(loop, t / span), on: true };
      t -= span;
      if (t <= JUMP) {
        const next = this.loops[i + 1] ?? loop;
        const a = along(loop, 1);
        const b = along(next, 0);
        const k = t / JUMP;
        return { x: a.x + (b.x - a.x) * k, y: a.y + (b.y - a.y) * k, on: false };
      }
      t -= JUMP;
    }
    const last = this.loops[n - 1];
    return { ...along(last, 1), on: true };
  }

  // Add to the trace everything the pen covered up to progress u.
  draw(u: number) {
    if (!this.ready || u <= this.drawn) return;
    const ctx = this.ctx;
    ctx.save();
    ctx.strokeStyle = this.color;
    ctx.lineWidth = 1.6;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.shadowColor = this.color;
    ctx.shadowBlur = 10;
    const steps = Math.max(2, Math.ceil((u - this.drawn) * 900));
    let prev = this.pen(this.drawn);
    ctx.beginPath();
    for (let s = 1; s <= steps; s++) {
      const p = this.pen(this.drawn + ((u - this.drawn) * s) / steps);
      if (p.on && prev.on) {
        ctx.moveTo(prev.x, prev.y);
        ctx.lineTo(p.x, p.y);
      }
      prev = p;
    }
    ctx.stroke();
    ctx.restore();
    this.drawn = u;
  }

  setOpacity(k: number) {
    this.canvas.style.opacity = String(k);
  }

  clear() {
    this.ctx.clearRect(0, 0, this.width, this.height);
    this.drawn = 0;
  }
}

function along(loop: Loop, f: number): Pt {
  let target = loop.length * Math.min(1, Math.max(0, f));
  for (let i = 1; i < loop.pts.length; i++) {
    const a = loop.pts[i - 1];
    const b = loop.pts[i];
    const d = Math.hypot(b.x - a.x, b.y - a.y);
    if (target <= d) {
      const k = d > 0 ? target / d : 0;
      return { x: a.x + (b.x - a.x) * k, y: a.y + (b.y - a.y) * k };
    }
    target -= d;
  }
  return loop.pts[loop.pts.length - 1];
}

// Marching squares: the closed outlines of the cells where `inside` holds, in sample
// units, each loop closed (first point repeated last) and thinned a little.
function traceLoops(cols: number, rows: number, inside: (x: number, y: number) => boolean): Pt[][] {
  // edge midpoints of cell (x, y): 0 top, 1 right, 2 bottom, 3 left
  const mid = (x: number, y: number, e: number): Pt =>
    e === 0 ? { x: x + 0.5, y } : e === 1 ? { x: x + 1, y: y + 0.5 } : e === 2 ? { x: x + 0.5, y: y + 1 } : { x, y: y + 0.5 };
  const key = (p: Pt) => `${p.x * 2},${p.y * 2}`;
  const next = new Map<string, Pt>();
  const segment = (x: number, y: number, a: number, b: number) => {
    const p = mid(x, y, a);
    const q = mid(x, y, b);
    next.set(key(p), q);
  };
  for (let y = -1; y < rows; y++) {
    for (let x = -1; x < cols; x++) {
      const tl = inside(x, y) ? 8 : 0;
      const tr = inside(x + 1, y) ? 4 : 0;
      const br = inside(x + 1, y + 1) ? 2 : 0;
      const bl = inside(x, y + 1) ? 1 : 0;
      // corners sit on sample centres, so shift the cell by half a sample
      const cx = x + 0.5;
      const cy = y + 0.5;
      switch (tl | tr | br | bl) {
        case 1: segment(cx, cy, 3, 2); break;
        case 2: segment(cx, cy, 2, 1); break;
        case 3: segment(cx, cy, 3, 1); break;
        case 4: segment(cx, cy, 1, 0); break;
        case 5: segment(cx, cy, 3, 0); segment(cx, cy, 1, 2); break;
        case 6: segment(cx, cy, 2, 0); break;
        case 7: segment(cx, cy, 3, 0); break;
        case 8: segment(cx, cy, 0, 3); break;
        case 9: segment(cx, cy, 0, 2); break;
        case 10: segment(cx, cy, 0, 1); segment(cx, cy, 2, 3); break;
        case 11: segment(cx, cy, 0, 1); break;
        case 12: segment(cx, cy, 1, 3); break;
        case 13: segment(cx, cy, 1, 2); break;
        case 14: segment(cx, cy, 2, 3); break;
        default: break;
      }
    }
  }
  const loops: Pt[][] = [];
  const seen = new Set<string>();
  for (const [startKey] of next) {
    if (seen.has(startKey)) continue;
    const pts: Pt[] = [];
    let k = startKey;
    let guard = 0;
    while (!seen.has(k) && guard++ < 200000) {
      seen.add(k);
      const [px, py] = k.split(',').map(Number);
      pts.push({ x: px / 2, y: py / 2 });
      const q = next.get(k);
      if (!q) break;
      k = key(q);
    }
    if (pts.length < 6) continue; // specks
    pts.push(pts[0]);
    // keep every other point: the raster is already finer than the line we draw
    loops.push(pts.filter((_, i) => i % 2 === 0 || i === pts.length - 1));
  }
  return loops;
}
